import { FormikProps, useFormik } from 'formik';
import { ICreatePersonaReq, IPersonaFormik } from 'entities/persona';
import clsx from 'clsx';
import FormError from 'components/Forms/FormError/FormError';
import React, { FC, useState } from 'react';
import { AddStarters } from './AddStarters';
import { PERSONA_DEFINITION_CHAR_LIMIT } from 'shared/model';
import { ChooseModelAiModal } from 'features/choose-model-ai';
import { engines } from 'entities/engine';
import { VscChevronDown } from 'react-icons/vsc';

type Props = {
  formik: FormikProps<IPersonaFormik>;
  classNameBg?: string;
};

export const PersonaDataForm: FC<Props> = ({
  formik,
  classNameBg = 'ag-bg-neutral-800'
}) => {
  const [openChoseAiModelModal, setOpenChoseAiModelModal] = useState(false);
  return (
    <div className="ag-space-y-10">
      <div className="ag-flex ag-flex-col ag-relative">
        <label className="ag-mb-4 ag-text-white" htmlFor="name">
          <div>Name</div>
          <div className="ag-text-sm ag-text-neutral-400">
            What’s the name of your Video Agent?
          </div>
        </label>

        <input
          className={clsx(
            classNameBg,
            'ag-border ag-border-neutral-600 ag-py-2 ag-px-3 ag-rounded-md ag-text-sm ag-outline-none ag-ring-0 focus:ag-outline-none focus:ag-ring-0',
            formik.errors.name && formik.touched.name
              ? 'ag-text-red-900  focus:ag-border-red-500'
              : 'ag-text-white placeholder:ag-text-gray-400 focus:ag-border-primary-800'
          )}
          placeholder="My Video Agent"
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          value={formik.values.name}
          name="name"
          id="name"
        />
        {formik.errors.name && formik.touched.name && (
          <FormError>{formik.errors.name}</FormError>
        )}
      </div>
      <div className="ag-flex ag-flex-col">
        <label className="ag-mb-4 ag-text-white" htmlFor="description">
          <div>Description</div>
          <div className="ag-text-sm ag-text-neutral-400">
            In a few words, describe your Video Agent.
          </div>
        </label>
        <textarea
          className={clsx(
            classNameBg,
            'ag-border ag-border-neutral-600 ag-py-2 ag-px-3 ag-rounded-md ag-text-sm ag-outline-none ag-ring-0 focus:ag-outline-none focus:ag-ring-0',
            formik.errors.description && formik.touched.description
              ? 'ag-text-red-900  focus:ag-border-red-500'
              : 'ag-text-white placeholder:ag-text-gray-400 focus:ag-border-primary-800'
          )}
          placeholder="For example, “An expert in digital marketing strategies.”"
          name="description"
          id="description"
          rows={3}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          value={formik.values.description}
        />
        <div className="ag-flex ag-items-center ag-justify-between">
          <div>
            {formik.errors.description && formik.touched.description && (
              <FormError>{formik.errors.description}</FormError>
            )}
          </div>
          <div className="ag-text-xs ag-text-neutral-400 ag-text-right ag-mt-1">
            {formik.values.description.length}/100 characters
          </div>
        </div>
      </div>
      <div className="ag-flex ag-flex-col ag-relative">
        <label className="ag-mb-4 ag-text-white" htmlFor="name">
          <div>Model</div>
          <div className="ag-text-sm ag-text-neutral-400">
            Select the AI model that powers your agent&apos;s responses and
            interactions
          </div>
        </label>
        <div className="ag-relative">
          <input
            className={clsx(
              classNameBg,
              'ag-border ag-w-full ag-border-neutral-600 ag-py-2 ag-px-3 ag-rounded-md ag-text-sm ag-outline-none ag-ring-0 focus:ag-outline-none focus:ag-ring-0',
              formik.errors.name && formik.touched.name
                ? 'ag-text-red-900  focus:ag-border-red-500'
                : 'ag-text-white placeholder:ag-text-gray-400 focus:ag-border-primary-800'
            )}
            placeholder="My Video Agent"
            onClick={() => setOpenChoseAiModelModal(true)}
            value={
              engines.find((model) => model.value === formik.values.model)?.name
            }
            name="model"
            id="name"
            readOnly
          />
          <VscChevronDown
            className={`ag-right-2 ag-transform ag-top-1/2 -ag-translate-y-1/2 ag-absolute ${
              openChoseAiModelModal && 'ag-rotate-180'
            } `}
          />
        </div>

        {formik.errors.name && formik.touched.name && (
          <FormError>{formik.errors.name}</FormError>
        )}
      </div>
      <div className="ag-flex ag-flex-col">
        <label className="ag-mb-4 ag-text-white" htmlFor="definition">
          <div>Instructions (Optional)</div>
          <div className="ag-text-sm ag-text-neutral-400">
            Define how your video agent will interact with users. Provide
            detailed guidance on the Video Agent&rsquo;s role, personality and
            expertise.
          </div>
        </label>
        <textarea
          className={clsx(
            classNameBg,
            'ag-border ag-border-neutral-600 ag-py-2 ag-px-3 ag-rounded-md ag-text-sm ag-outline-none ag-ring-0 focus:ag-outline-none focus:ag-ring-0 ag-scrollbar-none',
            formik.errors.definition && formik.touched.definition
              ? 'ag-text-red-900  focus:ag-border-red-500'
              : 'ag-text-white placeholder:ag-text-gray-400 focus:ag-border-primary-800'
          )}
          placeholder={`For example, "Be a friendly and knowledgeable science tutor focused on physics. Explain topics like force and energy in an easy-to-understand way. If a user asks about a topic outside of physics, politely steer the conversation back to physics by saying something like, 'I’m here to help with physics topics. Can we go back to discussing force and energy?' Keep the tone upbeat and supportive to make learning fun and engaging."`}
          name="definition"
          id="definition"
          rows={6}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          value={formik.values.definition}
        />
        <div className="ag-flex ag-items-center ag-justify-between">
          <div>
            {formik.errors.definition && formik.touched.definition && (
              <FormError>{formik.errors.definition}</FormError>
            )}
          </div>
          <div className="ag-text-xs ag-text-neutral-400 ag-text-right ag-mt-1">
            {formik.values.definition.length}/{PERSONA_DEFINITION_CHAR_LIMIT}{' '}
            characters
          </div>
        </div>
      </div>

      {/* <AddIntroMessages
        introMessages={formik.values.introMessages}
        onChange={(value) => formik.setFieldValue('introMessages', value)}
      /> */}

      <div className="ag-flex ag-flex-col">
        <label className="ag-mb-4 ag-text-white" htmlFor="greeting">
          <div>Greeting (Optional)</div>
          <div className="ag-text-sm ag-text-neutral-400">
            This is the first thing they’ll say when starting a new
            conversation.
          </div>
        </label>
        <textarea
          className={clsx(
            classNameBg,
            'ag-border ag-border-neutral-600 ag-py-2 ag-px-3 ag-rounded-md ag-text-sm ag-outline-none ag-ring-0 focus:ag-outline-none focus:ag-ring-0',
            formik.errors.greeting && formik.touched.greeting
              ? 'ag-text-red-900  focus:ag-border-red-500'
              : 'ag-text-white placeholder:ag-text-gray-400 focus:border-primary-800'
          )}
          placeholder={`For example, “Hello! I'm your personal assistant. How can I assist you today?”`}
          name="greeting"
          id="greeting"
          rows={5}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          value={formik.values.greeting}
        />
        <div className="ag-flex ag-items-center ag-justify-between">
          <div>
            {formik.errors.greeting && formik.touched.greeting && (
              <FormError>{formik.errors.greeting}</FormError>
            )}
          </div>
          <div className="ag-text-xs ag-text-neutral-400 ag-text-right ag-mt-1">
            {formik.values.greeting.length}/500 characters
          </div>
        </div>
      </div>

      <AddStarters
        starterQuestions={formik.values.starterQuestions}
        onChange={(value) => formik.setFieldValue('starterQuestions', value)}
        classNameBg={classNameBg}
      />

      <ChooseModelAiModal
        models={engines.map((item) => ({
          value: item.value,
          name: item.name,
          description: item.description,
          icon: item.iconBlack
        }))}
        onChoose={(value) => formik.setFieldValue('model', value)}
        setOpen={setOpenChoseAiModelModal}
        isOpen={openChoseAiModelModal}
      />
    </div>
  );
};
